import React, { useState, useEffect } from "react";
import axios from "../../utils/axiosInstance";
import Button from "../../components/ui/Button";
import TaskForm from "../../components/TaskManagement/forms/TaskForm";

const TaskEditor = ({ isOpen, onClose, task, onSaved }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    weight: 1,
    estimatedHours: "",
    tags: [],
    subTasks: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && task) {
      // Convert subtaskRefs to subTasks format
      const subTasks = (task.subtaskRefs || []).map((ref, index) => {
        const subtask = typeof ref.subtaskId === 'object' ? ref.subtaskId : { _id: ref.subtaskId };
        return {
          id: subtask._id,
          _id: subtask._id,
          name: subtask.name || "",
          description: subtask.description || "",
          weight: ref.weight || subtask.defaultWeight || subtask.weight || 1,
          estimatedHours: subtask.defaultEstimatedHours || subtask.estimatedHours || "",
          registryId: subtask._id,
          sortOrder: index
        };
      });

      setFormData({
        name: task.name || "",
        description: task.description || "",
        weight: task.defaultWeight || task.weight || 1,
        estimatedHours: task.defaultEstimatedHours || task.estimatedHours || "",
        tags: task.tags || [],
        showDescriptionInTimeReport: task.showDescriptionInTimeReport || false,
        requireDescriptionInTimeReport: task.requireDescriptionInTimeReport || false,
        photosNotRequiredInTimeReport: task.photosNotRequiredInTimeReport || false,
        subTasks
      });
    } else if (isOpen && !task) {
      setFormData({
        name: "",
        description: "",
        weight: 1,
        estimatedHours: "",
        tags: [],
        showDescriptionInTimeReport: false,
        requireDescriptionInTimeReport: false,
        photosNotRequiredInTimeReport: false,
        subTasks: []
      });
    }
  }, [isOpen, task]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name?.trim()) {
      alert("Task name is required");
      return;
    }

    setSaving(true);

    try {
      // Convert subTasks array to subtaskRefs format for backend
      const subtaskRefs = formData.subTasks.map((st, index) => ({
        subtaskId: st.registryId || st._id,
        weight: st.weight || 1,
        sortOrder: index
      }));

      const payload = {
        name: formData.name,
        description: formData.description,
        defaultWeight: parseFloat(formData.weight) || 1,
        defaultEstimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : undefined,
        tags: Array.isArray(formData.tags) 
          ? formData.tags 
          : formData.tags.split(",").map(t => t.trim()).filter(Boolean),
        showDescriptionInTimeReport: formData.showDescriptionInTimeReport || false,
        requireDescriptionInTimeReport: formData.requireDescriptionInTimeReport || false,
        photosNotRequiredInTimeReport: formData.photosNotRequiredInTimeReport || false,
        subtaskRefs
      };

      if (task) {
        await axios.put(`/registry/tasks/${task._id}`, payload);
      } else {
        await axios.post("/registry/tasks", payload);
      }

      onSaved && onSaved();
      onClose();
    } catch (error) {
      console.error("Error saving task:", error);
      alert("Failed to save task");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">
            {task ? "Edit Task" : "New Task"}
          </h2>

          <form onSubmit={handleSubmit}>
            <TaskForm
              value={formData}
              onChange={setFormData}
              mode="registry"
              showSaveToRegistry={false}
              disabled={saving}
            />

            <div className="flex justify-end gap-2 pt-4 mt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="solid" disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TaskEditor;
